import { cn, Icon, type SvgIcon } from '@bridebook/aura';
import { Loader } from '@bridebook/aura/src/icons';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';

const iconButtonVariants = cva(
  'relative inline-flex cursor-pointer items-center justify-center rounded-full border antialiased transition-all duration-150 ease-out focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none',
  {
    variants: {
      variant: {
        grey: 'border-transparent bg-space-04 hover:bg-space-08 active:bg-space-15 disabled:bg-space-04 disabled:text-space-40',
        white:
          'border-transparent bg-white hover:bg-space-04 active:bg-space-08 disabled:bg-white disabled:text-space-40',
        outline:
          'border-space-08 bg-white text-space hover:bg-space-04 active:bg-space-08 disabled:bg-white disabled:text-space-40',
      },
      size: {
        sm: 'size-[32px]',
        standard: 'size-[40px]',
      },
    },
  },
);

const iconVariants = cva('', {
  variants: {
    size: {
      sm: 'size-[16px]',
      standard: 'size-[24px]',
    },
  },
});

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  /**
   * If true, the component will render as a Slot component from Radix. This is useful for creating button-styled links or other elements.
   */
  asChild?: boolean;
  children?: React.ReactNode;
  /**
   * Icon to display.
   */
  icon: SvgIcon;
  /**
   * Show loading spinner and disable the button during loading state.
   */
  loading?: boolean;
  size?: 'sm' | 'standard';
  classNames?: {
    icon?: string;
    loadingIcon?: string;
  };
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      classNames,
      loading = false,
      children,
      disabled,
      variant = 'grey',
      size = 'standard',
      asChild = false,
      icon,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || disabled}
        {...props}>
        <Slottable>{children}</Slottable>
        {loading ? (
          <Icon
            icon={Loader}
            className={cn(iconVariants({ size }), classNames?.loadingIcon, 'animate-spin')}
          />
        ) : (
          <Icon icon={icon} className={cn(iconVariants({ size }), classNames?.icon)} />
        )}
      </Comp>
    );
  },
);
IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };
